var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { showNotification } from "slices/Notifications";
import { customBaseQuery } from "../common";
import { isApiResponse } from "./typeGuards";
// Module-level guard: at most one /authenticate request is in flight at a time. This prevents
// SessionOutOfSync errors caused by multiple 401 responses (e.g. from parallel dashboard API
// calls) each independently triggering /authenticate.
let inFlightReauth = null;
// Moved from common.ts
export function re_authenticate(csrf_token, api) {
    return __awaiter(this, void 0, void 0, function* () {
        if (inFlightReauth) {
            // An /authenticate request is already in flight, so do not start another one - and do not
            // await the one that is running either. This function is re-entrant: the /authenticate request
            // goes through customBaseQuery, so its own response can fail CSRF validation or come back as a
            // 401, and the error handlers for it call re_authenticate again from inside the call that is
            // still running. Returning the in-flight promise there would make that promise await itself:
            // it would never settle, the reset below would never run, and every later caller in this tab
            // would then await a promise that can not complete.
            //
            // Nobody depends on the re-authentication having finished when this returns - it ends in a
            // redirect - so returning without waiting is enough for callers that are merely concurrent.
            return;
        }
        inFlightReauth = (() => __awaiter(this, void 0, void 0, function* () {
            try {
                const reauth_result = yield customBaseQuery({ url: "authenticate", body: { frontend_action: "login", csrf_token: csrf_token } }, api, { service: "authn" });
                if (reauth_result.data && isApiResponse(reauth_result.data)) {
                    if (reauth_result.data.payload.location) {
                        globalThis.location.href = reauth_result.data.payload.location;
                    }
                }
            }
            finally {
                inFlightReauth = null;
            }
        }))();
        return inFlightReauth;
    });
}
export function handleBaseQueryError(result, csrf_token, api, state) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        // FetchBaseQuery errors
        if (typeof result.error.status === "number") {
            if (result.error.status === 401) {
                // If we get a 401, authenticate for login
                yield re_authenticate(csrf_token, api);
            }
            else if (result.error.status === 0 && state.config.authn_service_url) {
                // re-implementing handling of status 0 errors
                // redirect to login with return to where we are now
                const current_page = globalThis.location.href;
                globalThis.location.href =
                    state.config.authn_service_url + "/services/authn/login?next=" + encodeURIComponent(current_page);
            }
            else {
                // should be errors only, i.e. 400 and 500 series
                api.dispatch(showNotification({ message: "HTTP " + result.error.status, level: "error" }));
            }
        }
        else if (((_a = result.error) === null || _a === void 0 ? void 0 : _a.status) === "FETCH_ERROR") {
            // fetchBaseQuery wrapped errors
            api.dispatch(showNotification({ message: "general.failed_to_fetch", level: "error" }));
        }
        else {
            api.dispatch(showNotification({ message: result.error.status + " " + result.error.error, level: "error" }));
        }
    });
}
export function handleApiError(data, meta, csrf_token, api) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        // validation errors from backend has slightly different format
        if (((_b = (_a = data.payload.error) === null || _a === void 0 ? void 0 : _a.csrf_token) === null || _b === void 0 ? void 0 : _b[0]) === "CSRF failed to validate") {
            yield re_authenticate(csrf_token, api);
        }
        else if ((_c = data.payload.error) === null || _c === void 0 ? void 0 : _c.nin) {
            api.dispatch(showNotification({ message: data.payload.error.nin[0], level: "error" }));
        }
        else if (data.payload.message === "resetpw.captcha-already-completed") {
            // captcha already completed, no need to show error
        }
        else if (data.payload.message === "authn_status.must-authenticate") {
            // set frontend_action and trigger re-authentication modal
            // use string types to avoid circular dependencies
            const metaObj = data.meta;
            if (metaObj === null || metaObj === void 0 ? void 0 : metaObj.frontend_action) {
                api.dispatch({ type: "authn/setFrontendActionAndState", payload: { frontend_action: metaObj.frontend_action } });
            }
            api.dispatch({ type: "authn/setReAuthenticate", payload: true });
        }
        else {
            const msg = data.payload.message || "error_in_form";
            api.dispatch(showNotification({ message: msg, level: "error" }));
        }
        // For RTK query purposes return the result as error
        return {
            error: data,
            meta: meta,
        };
    });
}
